import React from 'react';
import { Modal, Icon, Header, Label, Segment, Grid, Statistic, Button } from 'semantic-ui-react';
import { ContractStatusHistoryDto } from '../../services/contracts/ContractService';
import { useAuth } from '../../context/AuthContext';
import { getContractStatusText } from '../../utils/contractStatusUtils';

interface StatusHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  history: ContractStatusHistoryDto[];
  contractId: string;
}

const StatusHistoryModal: React.FC<StatusHistoryModalProps> = ({
  isOpen,
  onClose,
  history,
  contractId
}) => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Admin');
  
  // Calcular tempo em cada status
  const calculateDuration = (current: string, next?: string) => {
    try {
      const start = new Date(current);
      const end = next ? new Date(next) : new Date();
      
      const diffMs = end.getTime() - start.getTime();
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (days > 0) return `${days}d ${hours}h`;
      if (hours > 0) return `${hours}h ${minutes}min`;
      return `${minutes}min`;
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} às ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  // Calcular tempo total desde a primeira mudança
  const getTotalTime = () => {
    if (history.length === 0) return '-';
    try {
      const oldest = history[history.length - 1];
      return calculateDuration(oldest.changedAt);
    } catch {
      return '-';
    }
  };

  // Calcular tempo no status atual
  const getCurrentStatusTime = () => {
    if (history.length === 0) return '-';
    try {
      return calculateDuration(history[0].changedAt);
    } catch {
      return '-';
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} size="large">
      <Modal.Header>
        <Icon name="history" />
        Histórico Completo de Status
        <div style={{ fontSize: '0.85em', fontWeight: 'normal', color: '#666', marginTop: '5px' }}>
          Contrato ID: {contractId}
        </div>
      </Modal.Header>
      
      <Modal.Content scrolling>
        {history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <Icon name="info circle" size="huge" color="grey" />
            <div style={{ marginTop: '15px', fontSize: '1.1em' }}>
              Nenhum histórico de status disponível
            </div>
          </div>
        ) : (
          <>
            <div style={{ position: 'relative', paddingLeft: '40px' }}>
              {/* Timeline vertical */}
              <div style={{
                position: 'absolute',
                left: '20px',
                top: '0',
                bottom: '0',
                width: '2px',
                background: 'linear-gradient(to bottom, #2ecc71, #3498db, #95a5a6)'
              }} />
              
              {history.map((item, index) => (
                <div 
                  key={item.id} 
                  style={{ 
                    position: 'relative', 
                    marginBottom: '24px',
                    paddingBottom: '20px',
                    borderBottom: index < history.length - 1 ? '1px solid #eee' : 'none'
                  }}
                >
                  {/* Ponto na timeline */}
                  <div style={{
                    position: 'absolute',
                    left: '-29px',
                    top: '2px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    background: index === 0 ? '#2ecc71' : '#3498db',
                    border: '3px solid white',
                    boxShadow: '0 0 0 1px #ddd',
                    zIndex: 1
                  }} />
                  
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <Header size="small" style={{ margin: 0 }}>
                        {getContractStatusText(item.status)}
                      </Header>
                      {index === 0 && (
                        <Label color="green" size="small">
                          <Icon name="check" />
                          Status Atual
                        </Label>
                      )}
                    </div>
                    
                    <div style={{ color: '#666', fontSize: '0.9em', marginBottom: '8px' }}>
                      <Icon name="calendar" />
                      {formatDateTime(item.changedAt)}
                      {index < history.length - 1 && (
                        <Label size="mini" style={{ marginLeft: '10px' }} color="grey">
                          <Icon name="clock outline" />
                          Duração: {calculateDuration(item.changedAt, history[index - 1]?.changedAt)}
                        </Label>
                      )}
                      {index === 0 && (
                        <Label size="mini" style={{ marginLeft: '10px' }} color="green">
                          <Icon name="clock outline" />
                          há {calculateDuration(item.changedAt)}
                        </Label>
                      )}
                    </div>
                    
                    {/* CONDICIONAL: Apenas Admin vê quem mudou */}
                    {isAdmin && item.changedByUserName && (
                      <div style={{ color: '#666', fontSize: '0.9em', marginBottom: '6px' }}>
                        <Icon name="user" />
                        <strong>{item.changedByUserName}</strong>
                        {item.changedByUserEmail && (
                          <span style={{ color: '#999', marginLeft: '6px' }}>
                            ({item.changedByUserEmail})
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Não-Admin: Exibe apenas texto genérico */}
                    {!isAdmin && (
                      <div style={{ color: '#999', fontSize: '0.85em', marginBottom: '6px' }}>
                        <Icon name="user outline" />
                        Alterado por: Sistema/Usuário
                      </div>
                    )}
                    
                    {item.changeReason && (
                      <div style={{ 
                        color: '#555', 
                        fontSize: '0.85em', 
                        fontStyle: 'italic',
                        marginTop: '6px',
                        padding: '8px 12px',
                        background: '#f9f9f9',
                        borderRadius: '4px',
                        borderLeft: '3px solid #3498db'
                      }}>
                        <Icon name="info circle" color="blue" />
                        {item.changeReason}
                      </div>
                    )}

                    {item.groupId && (
                      <div style={{ color: '#888', fontSize: '0.8em', marginTop: '6px' }}>
                        <Icon name="sitemap" />
                        <span style={{ marginLeft: '4px' }}>GroupId: {item.groupId}</span>
                      </div>
                    )}
                    
                    {item.previousStatus != null && (
                      <div style={{ 
                        color: '#999', 
                        fontSize: '0.8em', 
                        marginTop: '8px'
                      }}>
                        <Icon name="arrow left" />
                        Anterior: {getContractStatusText(item.previousStatus ?? undefined)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Estatísticas do histórico */}
            <Segment style={{ marginTop: '30px', background: '#f8f9fa' }}>
              <Header size="small" style={{ marginBottom: '15px' }}>
                <Icon name="chart bar" />
                Resumo Estatístico
              </Header>
              <Grid columns={3} divided stackable>
                <Grid.Column textAlign="center">
                  <Statistic size="mini">
                    <Statistic.Value>
                      <Icon name="exchange" size="small" />
                      {history.length}
                    </Statistic.Value>
                    <Statistic.Label>Mudanças de Status</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column textAlign="center">
                  <Statistic size="mini">
                    <Statistic.Value>
                      <Icon name="hourglass half" size="small" />
                      {getTotalTime()}
                    </Statistic.Value>
                    <Statistic.Label>Tempo Total</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column textAlign="center">
                  <Statistic size="mini" color="green">
                    <Statistic.Value>
                      <Icon name="clock" size="small" />
                      {getCurrentStatusTime()}
                    </Statistic.Value>
                    <Statistic.Label>No Status Atual</Statistic.Label>
                  </Statistic>
                </Grid.Column>
              </Grid>
            </Segment>
          </>
        )}
      </Modal.Content>
      
      <Modal.Actions>
        <Button onClick={onClose} primary>
          <Icon name="check" />
          Fechar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default StatusHistoryModal;
